import {Injectable} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators, AbstractControl} from '@angular/forms';

import {PatientService} from './patient.service';
import {CustomFieldDefinition} from '../../../types';


/**
 * Service for managing custom field definitions, forms, and validation.
 * Handles loading, building forms, and value operations for custom fields.
 */
@Injectable()
export class CustomFieldsManagerService {
  /**
   * Injects dependencies.
   */
  public constructor(
    private formBuilder: FormBuilder,
    private patientService: PatientService
  ) {
  }

  /**
   * Loads custom field definitions from the API.
   *
   * @returns Promise resolving to array of definitions.
   */
  public loadDefinitions(): Promise<CustomFieldDefinition[]> {
    return this.patientService.loadCustomFieldDefinitions();
  }

  /**
   * Builds a FormGroup for the given custom field definitions.
   *
   * @param definitions Array of custom field definitions.
   * @returns FormGroup with controls for each custom field.
   */
  public buildForm(definitions: CustomFieldDefinition[]): FormGroup {
    const group: {[key: string]: FormControl} = {};

    for (const def of definitions) {
      const validators: ValidatorFn[] = [];

      // add required validator if defined
      if (def.required) {
        validators.push(Validators.required);
      }

      // add regex validator if defined for text fields
      if (def.type === 'text' && def.validation && def.validation.regex) {
        validators.push(this.createRegexValidator(def.validation.regex, def.validation.regexMod));
      }

      // boolean fields default to false, others to null
      const defaultValue = def.type === 'boolean' ? false : null;
      group[def.key] = new FormControl(defaultValue, validators);
    }

    return this.formBuilder.group(group);
  }

  /**
   * Creates an empty FormGroup (used before definitions are loaded).
   *
   * @returns Empty FormGroup.
   */
  public createEmptyForm(): FormGroup {
    return this.formBuilder.group({});
  }

  /**
   * Renders custom field values into the form.
   *
   * @param form The custom fields FormGroup.
   * @param customData The custom data object from patient.
   * @param definitions Array of custom field definitions.
   */
  public renderToForm(form: FormGroup, customData: any, definitions: CustomFieldDefinition[]): void {
    if (!customData || !form) {
      return;
    }

    for (const def of definitions) {
      if (customData.hasOwnProperty(def.key) && form.contains(def.key)) {
        form.get(def.key).setValue(customData[def.key]);
      }
    }
  }

  /**
   * Collects custom field values from the form.
   *
   * @param form The custom fields FormGroup.
   * @param definitions Array of custom field definitions.
   * @returns Object containing custom field values.
   */
  public collectValues(form: FormGroup, definitions: CustomFieldDefinition[]): any {
    const custom: any = {};

    for (const def of definitions) {
      const control = form.get(def.key);
      if (control) {
        custom[def.key] = control.value;
      }
    }

    return custom;
  }

  /**
   * Marks all custom fields as touched to show validation errors.
   *
   * @param form The custom fields FormGroup.
   */
  public markAllAsTouched(form: FormGroup): void {
    if (!form) {
      return;
    }

    Object.keys(form.controls).forEach(key => {
      form.get(key).markAsTouched();
    });
  }

  /**
   * Checks if a custom field is invalid and touched/dirty.
   *
   * @param form The custom fields FormGroup.
   * @param key The field key.
   * @returns true if field is invalid and touched/dirty.
   */
  public isFieldInvalid(form: FormGroup, key: string): boolean {
    const control = form.get(key);
    return control && control.invalid && (control.dirty || control.touched);
  }

  /**
   * Gets the validation hint for a custom field.
   *
   * @param def The custom field definition.
   * @param fallbackText Fallback text if no hint is defined.
   * @returns The validation hint text.
   */
  public getValidationHint(def: CustomFieldDefinition, fallbackText: string): string {
    if (def.validation && def.validation.hint) {
      return def.validation.hint;
    }
    return fallbackText;
  }

  /**
   * Updates validators for custom fields based on visibility in the layout.
   * Clears validators for fields that are not visible, so they don't block form submission.
   *
   * @param form The custom fields FormGroup.
   * @param definitions Array of custom field definitions.
   * @param visibleCustomFields Array of visible custom field keys from the layout.
   */
  public updateValidatorsForLayout(form: FormGroup, definitions: CustomFieldDefinition[], visibleCustomFields: string[]): void {
    for (const def of definitions) {
      const control = form.get(def.key);

      if (!control) {
        continue;
      }

      if (!visibleCustomFields.includes(def.key)) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    }
  }

  /**
   * Gets a custom field definition by key.
   *
   * @param definitions Array of definitions.
   * @param key The field key.
   * @returns The definition or undefined.
   */
  public getDefinition(definitions: CustomFieldDefinition[], key: string): CustomFieldDefinition | undefined {
    return definitions.find(d => d.key === key);
  }

  /**
   * Creates a regex validator function.
   *
   * @param pattern The regex pattern string.
   * @param modifier The regex modifier string (e.g., 'i' for case-insensitive).
   * @returns ValidatorFn
   */
  private createRegexValidator(pattern: string, modifier?: string): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!control.value) {
        // empty values are valid (fields are optional)
        return null;
      }

      try {
        const regex = new RegExp(pattern, modifier || '');
        const valid = regex.test(control.value);
        return valid ? null : {pattern: {value: control.value}};
      } catch (e) {
        // invalid regex pattern - treat as valid to not block user
        return null;
      }
    };
  }
}

